import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Divider, CircularProgress } from '@mui/material';
import { useAuth } from '../../../context/AuthContext';
import { useOutletContext } from 'react-router-dom';
import { Property } from '../../../types/Property';
import { useProperty } from '../../../context/PropertyContext';
import { getPhotosFromIndexedDB, deleteAllPhotosFromIndexedDB } from '../../../utils/indexDB';
import { useCompany } from '../../../context/CompanyContext';

type PublishOutletContext = {
    handleExit: () => void;
    addressDetails: {
        country: string;
        province: string;
        county: string;
        district: string;
        streetAddress: string;
        postalCode: string;
        nearbyLandmark: string;
        googleMapsUrl: string;
        latitude: number;
        longitude: number;
        Zone: string;
    };
    property: Property; 
    propertyCurrency: string;
    selectedRealtorId: number | null
};

const categoryMapping: Record<string, number> = {
    'Casa': 1,
    'Apartamento': 2,
    'Habitaciones': 3,
    'Lote': 4,
    'Condominio': 5,
    'Edificio': 6,
    'Torre': 7,
    'Finca': 8,
    'Local': 9,
    'Terreno': 10,
    'Casa independiente': 11,
    'Penthouse': 12,
    'Townhouse': 13,
    'Apartamento loft': 14,
    'Apartamento estudio': 15,
    'Casa de lujo': 16,
    'Moderna': 17,
    'Rústica': 18,
    'Propiedad Comercial': 19,
    'Oficentro': 20,
    'Mall': 21,
    'Mansión': 22,
};

const PublishPropertySuccessPage = () => {
    const { name, userToken, isAdmin } = useAuth();
    const { publishProperty, loading } = useProperty();
    const { handleExit, property, addressDetails, selectedRealtorId} = useOutletContext<PublishOutletContext>();
    const [skipHunter, setSkipHunter] = useState(false);
    const { company } = useCompany();

    useEffect(() => {
      const shouldSkipHunter = localStorage.getItem("skipHunter") === "true"
      setSkipHunter(shouldSkipHunter)
    }, [])


      const handlePublishAndExit = async () => {
        try {
           // Determinar qué ID de realtor usar
           let idRealtorToUse: number | undefined

           // Si es administrador y hay un realtor seleccionado, usar ese ID
           if (isAdmin && selectedRealtorId) {
             idRealtorToUse = selectedRealtorId
           } else {
             // Comportamiento original para brokers
             const realtorProfile = userToken?.user.profiles.find((profile) => profile.roleId === 3)
             const isLeader = realtorProfile?.realtor?.isLeader
             idRealtorToUse = realtorProfile?.realtor?.id
     
             // Si es líder y hay un realtor seleccionado, usar ese ID
             if (isLeader && selectedRealtorId) {
               idRealtorToUse = selectedRealtorId
             }
           }
     
           // Verificar que tenemos un ID de realtor válido
           if (!idRealtorToUse) {
             console.error("No se pudo determinar el ID del realtor a usar")
             return
           }

      const categoryId = categoryMapping[property.category] || 0

      const formData = new FormData()
      const photosFromDB = await getPhotosFromIndexedDB()

          console.log("property", property)

            formData.append('Code', property.code);
            formData.append('Title', property.title);
            formData.append('Description', property.description);
            formData.append('Dimension', property.dimension.toString());
            formData.append('Details', property.details);
            formData.append('Services', property.services);
            formData.append('Amenities', property.amenities);
            formData.append('Idmode', property.type === 'Venta' ? '1' : '2');
            formData.append('price', property.price.toString());
            formData.append('score', '0');
            formData.append('paymentFrequency', '');
            formData.append('IdCategory', categoryId.toString());
            formData.append('IdOwner', '');
            formData.append('OwnerName', name!);
            formData.append("IdRealtor", idRealtorToUse!.toString())
            formData.append('AddressDescription', property.addressDescription);
            formData.append('googleMaps', addressDetails.googleMapsUrl);
            formData.append('Latitude', addressDetails.latitude.toString());
            formData.append('Longitude', addressDetails.longitude.toString());
            formData.append('Zone', addressDetails.Zone);
            formData.append('County', property.county);
            formData.append('District', property.district);
            formData.append('ModuleStateId', '1');
            formData.append('Visibility', property.visibility);
            formData.append('ComissionType', property.commisionType.toString());
            formData.append('Commission', property.commision)

            if(company){
              formData.append("CompanyId", company.id.toString());
            }


            if (!skipHunter && property.hunter) {
              formData.append("IdHunter", property.hunter.toString())
              console.log("Enviando Hunter ID:", property.hunter)
            } else {
              console.log("Omitiendo el campo IdHunter")
            }
            formData.append("Currency", property.currency);

            photosFromDB.forEach((file, index) => {
                formData.append('Photos', file, `photo_${index}.jpg`);
            });

            await publishProperty(formData);
            await deleteAllPhotosFromIndexedDB();

            handleExit();
        } catch (error) {
            console.error('Error al publicar la propiedad:', error);
        }
    };

    return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            height: '100vh',
          }}
        >
          {/* Left section (Image) */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: { xs: '50vh', sm: '100%' },
            }}
          >
            <Box sx={{ width: '100%', height: '100%' }}>
              <img
                src="/assets/images/fondo1.jpg"
                alt="Success"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          </Box>
      
          {/* Right section (Text content) */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: '#004d40',
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: { xs: 'auto', sm: '100%' }, 
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                textAlign: 'center',
                flex: 1,
                p: { xs: 3, sm: 8 }, 
              }}
            >
              <Typography variant="h3" gutterBottom sx={{ mb: 2 }}>
                ¡Felicidades {name}!
              </Typography>
              <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                Has completado gran parte del proceso de publicación de tu propiedad. Estás a solo un paso de completar el proceso y compartir tu inmueble con potenciales interesados.
              </Typography>
              <Typography variant="body2" color="inherit" paragraph sx={{ mb: 2 }}>
                Si necesitas hacer más ajustes o añadir detalles adicionales, puedes hacerlo desde tu panel de control.
              </Typography>
            </Box>
      
            {/* Divider and Button */}
            <Box sx={{ width: '100%' }}>
              <Divider
                sx={{
                  backgroundColor: '#fff',
                  marginBottom: 2,
                  borderWidth: '2px',
                  borderStyle: 'solid',
                  borderColor: '#00796b',
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'end', p: 2, pb: { xs: 12, sm: 2},}}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePublishAndExit}
                  disabled={loading}
                  sx={{
                    background: 'linear-gradient(to right, #ff5722, #ff9800)',
                    color: '#fff',
                    boxShadow: 3,
                    '&:hover': {
                      background: 'linear-gradient(to right, #ff9800, #ff5722)',
                      boxShadow: 6,
                    },
                    padding: '10px 20px',
                    borderRadius: '8px',
                    width: '150px',
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                  ) : (
                    <span style={{ visibility: loading ? 'hidden' : 'visible' }}>
                      Terminar
                    </span>
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }      

export default PublishPropertySuccessPage
